package com.appsyde.contract.recommended

/**
 * Label et why (FR) contextuels pour chaque code. Partagé front/back.
 */
object RecommendedActionLabelsFr {

    fun buildUi(code: RecommendedActionCode, state: RecommendedActionState): RecommendedActionUi {
        val intent = state.intent?.trim()?.lowercase()?.replace(" ", "_") ?: ""
        val refundPref = state.detected["refund_preference"]?.trim()?.lowercase()
        val issueType = state.detected["issue_type"]?.trim()?.lowercase()
        val hasTracking = state.detected.containsKey("tracking_number")
        val hasOrder = state.detected.containsKey("order_number")

        return when (code) {
            RecommendedActionCode.ASK_ORDER_NUMBER -> RecommendedActionUi(
                code = code.code,
                labelFr = "Demander le n° de commande",
                whyFr = "Indispensable pour identifier la commande."
            )
            RecommendedActionCode.ASK_TRACKING_NUMBER -> RecommendedActionUi(
                code = code.code,
                labelFr = "Demander le n° de suivi",
                whyFr = "Utile pour vérifier l'acheminement du colis."
            )
            RecommendedActionCode.ASK_ACCOUNT_ID -> RecommendedActionUi(
                code = code.code,
                labelFr = "Demander l'identifiant de compte",
                whyFr = "Nécessaire pour retrouver le dossier client."
            )
            RecommendedActionCode.ASK_PHOTO_PROOF -> RecommendedActionUi(
                code = code.code,
                labelFr = "Demander une photo",
                whyFr = "Nécessaire pour valider l'incident."
            )
            RecommendedActionCode.CONFIRM_REFUND_PREFERENCE -> RecommendedActionUi(
                code = code.code,
                labelFr = "Confirmer la solution souhaitée",
                whyFr = "Remboursement, échange ou avoir."
            )
            RecommendedActionCode.REQUEST_LOGIN_IDENTIFIER -> RecommendedActionUi(
                code = code.code,
                labelFr = "Demander l'identifiant de connexion",
                whyFr = "Pour lancer la réinitialisation."
            )
            RecommendedActionCode.SEND_PASSWORD_RESET -> RecommendedActionUi(
                code = code.code,
                labelFr = "Envoyer la procédure de réinitialisation",
                whyFr = null
            )
            RecommendedActionCode.TRIAGE_BUG -> RecommendedActionUi(
                code = code.code,
                labelFr = "Collecter les informations nécessaires",
                whyFr = "Étapes, appareil, date, contexte."
            )
            RecommendedActionCode.VERIFY_DELIVERY_STATUS -> RecommendedActionUi(
                code = code.code,
                labelFr = if (hasTracking) "Vérifier le suivi de livraison" else "Vérifier le statut de livraison",
                whyFr = if (hasOrder || hasTracking) "Vérification possible avec les infos disponibles." else "Il manque des infos pour vérifier."
            )
            RecommendedActionCode.PREPARE_REPLY -> {
                val label = when (intent) {
                    "order_tracking" -> "Répondre sur le statut de livraison"
                    "billing_question" -> "Répondre sur la facturation"
                    "product_question" -> "Répondre à la question produit"
                    "damaged_product", "wrong_item", "missing_item" -> "Répondre au client"
                    else -> "Répondre au client"
                }
                val why = when {
                    state.missing.isNotEmpty() -> "Informations manquantes à collecter ou à intégrer dans la réponse."
                    state.toConfirm.isNotEmpty() -> "Éléments à confirmer avant de finaliser la réponse."
                    intent in listOf("damaged_product", "wrong_item", "missing_item", "refund_request", "cancellation") ->
                        "Contexte produit ou remboursement ; rédiger la réponse avec les infos disponibles."
                    else -> "Répondre avec les informations disponibles."
                }
                RecommendedActionUi(code = code.code, labelFr = label, whyFr = why)
            }
            RecommendedActionCode.PREPARE_RESOLUTION -> {
                val label = when (refundPref) {
                    "refund" -> "Proposer un remboursement"
                    "replacement" -> "Proposer un échange"
                    "store_credit" -> "Proposer un avoir"
                    else -> when (intent) {
                        "damaged_product" -> "Proposer une solution au produit endommagé"
                        "wrong_item" -> "Proposer l'échange du bon article"
                        "missing_item" -> "Proposer une solution pour l'article manquant"
                        "refund_request" -> "Traiter la demande de remboursement"
                        "cancellation" -> "Traiter la demande d'annulation"
                        "order_tracking" -> "Répondre sur le statut de livraison"
                        else -> "Répondre au client"
                    }
                }
                val why = when (refundPref) {
                    "refund" -> "Préférence détectée : remboursement."
                    "replacement" -> "Préférence détectée : échange."
                    "store_credit" -> "Préférence détectée : avoir."
                    else -> when (issueType) {
                        "damaged" -> "Incident : produit endommagé."
                        "wrong_item" -> "Incident : mauvais article."
                        "missing_item" -> "Incident : article manquant."
                        else -> null
                    }
                }
                RecommendedActionUi(code = code.code, labelFr = label, whyFr = why)
            }
        }
    }
}
